import React, { useState } from 'react';

const ERDGuidelines = ({ onNavigate }) => {
  const [activeSection, setActiveSection] = useState('entities');

  const sections = [
    { id: 'entities', title: 'Entities'},
    { id: 'relationships', title: 'Relationships' },
    { id: 'specializations', title: 'Specializations'},
    { id: 'unions', title: 'Unions'},
    { id: 'example', title: 'Complete Example' }
  ];

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-4">ERD Guidelines</h1>
        <p className="text-gray-600 mb-4">
          Comprehensive guide to Entity-Relationship Diagram notation and grammar.
        </p>
        
        {/* Quick Navigation */}
        <div className="flex flex-wrap gap-2">
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => scrollToSection(section.id)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                activeSection === section.id
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
                {section.title}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="grid lg:grid-cols-4 gap-6">
        {/* Sticky Table of Contents */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-lg p-4 sticky top-6">
            <h3 className="font-bold text-gray-800 mb-3">Table of Contents</h3>
            <nav className="space-y-2">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors duration-200 ${
                    activeSection === section.id
                      ? 'bg-blue-100 text-blue-700 font-medium'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                    {section.title}
                </button>
              ))}
            </nav>
            
            {/* Quick Action */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={() => onNavigate('editor')}
                className="w-full bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200"
              >
                Open ERD Editor
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-3 space-y-8">
          
          {/* Entities Section */}
          <section id="entities" className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              Entities
            </h2>
            
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-800 mb-2">General Format</h3>
                <pre className="bg-white border rounded p-3 text-sm overflow-x-auto">
{`[Entity]
entity_name [@entity_tag]
--
attribute_name1 [@attr_tag]
attribute_name2 [(sub_attributes)] [@attr_tag]`}
                </pre>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-3">Entity Tags</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start">
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-mono mr-2 mt-0.5">@weak</span>
                      <span>For weak entities</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-3">Attribute Tags</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start">
                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-mono mr-2 mt-0.5">@key</span>
                      <span>Key attributes</span>
                    </li>
                    <li className="flex items-start">
                      <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs font-mono mr-2 mt-0.5">@multivalued</span>
                      <span>Multivalued attributes</span>
                    </li>
                    <li className="flex items-start">
                      <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-mono mr-2 mt-0.5">@derived</span>
                      <span>Derived attributes</span>
                    </li>
                    <li className="flex items-start">
                      <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs font-mono mr-2 mt-0.5">@partial_key</span>
                      <span>Partial key (weak entities)</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-800 mb-2">Important Notes</h4>
                <ul className="text-yellow-700 space-y-1 text-sm">
                  <li>• Composite attributes: Use parentheses for sub-attributes, e.g., <code className="bg-yellow-100 px-1 rounded">address (street,city,state)</code></li>
                  <li>• Entities must have an attribute section (leave empty if no attributes)</li>
                  <li>• Entity and attribute names must not include spaces</li>
                </ul>
              </div>

              <div className="bg-gray-100 border border-gray-300 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-2">Example</h4>
                <pre className="bg-white border rounded p-3 text-sm overflow-x-auto">
{`[Entity]
Employee
--
FirstName
LastName
SSN @key
Address (street,city,state)
Phone @multivalued

[Entity]
Dependent @weak
--
name (firstname,lastname) @partial_key`}
                </pre>
              </div>
            </div>
          </section>

          {/* Relationships Section */}
          <section id="relationships" className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              Relationships
            </h2>
            
            <div className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="font-semibold text-green-800 mb-2">General Format</h3>
                <pre className="bg-white border rounded p-3 text-sm overflow-x-auto">
{`[Relationship]
relationship_name [@relationship_tag]
--
participant_entity_1 (cardinality) [@participation_tag]
participant_entity_2 (cardinality) [@participation_tag]
participant_entity_3 (cardinality) [@participation_tag]
--
relationship_attr_1 [(sub_attributes)] [@attr_tag]
relationship_attr_2 [(sub_attributes)] [@attr_tag]`}
                </pre>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-3">Relationship Tags</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start">
                      <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-xs font-mono mr-2 mt-0.5">@identifying</span>
                      <span>Identifying relationships</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-3">Cardinality</h4>
                  <ul className="space-y-2 text-sm">
                    <li><span className="font-mono bg-gray-200 px-2 py-1 rounded">1</span> - One</li>
                    <li><span className="font-mono bg-gray-200 px-2 py-1 rounded">N</span> - Many</li>
                    <li><span className="font-mono bg-gray-200 px-2 py-1 rounded">M</span> - Many (alternative)</li>
                  </ul>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-3">Participation Tags</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start">
                      <span className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded text-xs font-mono mr-2 mt-0.5">@total_participation</span>
                      <span>Total participation</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-800 mb-2">Important Notes</h4>
                <ul className="text-yellow-700 space-y-1 text-sm">
                  <li>• Relationship and attribute names must not include spaces</li>
                  <li>• Participation section is mandatory</li>
                  <li>• Relationship attributes section is optional</li>
                </ul>
              </div>

              <div className="bg-gray-100 border border-gray-300 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-2">Example</h4>
                <pre className="bg-white border rounded p-3 text-sm overflow-x-auto">
{`[Relationship]
works_for
--
Employee (1) 
Department (N)

[Relationship]
Has @identifying
--
Employee (1)
Dependent (N) @total_participation`}
                </pre>
              </div>
            </div>
          </section>

          {/* Specializations Section */}
          <section id="specializations" className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              Specializations
            </h2>
            
            <div className="space-y-6">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h3 className="font-semibold text-purple-800 mb-2">General Format</h3>
                <pre className="bg-white border rounded p-3 text-sm overflow-x-auto">
{`[Specialization]
super_category_entity @specialization_tag
--
sub_category_entity1
sub_category_entity2`}
                </pre>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-3">Specialization Tags</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start">
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-mono mr-2 mt-0.5">@disjoint</span>
                      <span>Disjoint specialization</span>
                    </li>
                    <li className="flex items-start">
                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-mono mr-2 mt-0.5">@overlapping</span>
                      <span>Overlapping specialization</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-800 mb-2">When to Use</h4>
                  <p className="text-blue-700 text-sm">
                    Use specialization when you have a general entity that can be categorized into more specific subtypes with their own unique attributes.
                  </p>
                </div>
              </div>

              <div className="bg-gray-100 border border-gray-300 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-2">Example</h4>
                <pre className="bg-white border rounded p-3 text-sm overflow-x-auto">
{`[Specialization]
Employee @disjoint
--
HourlyEmployee
SalaryEmployee`}
                </pre>
              </div>
            </div>
          </section>

          {/* Unions Section */}
          <section id="unions" className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              Unions
            </h2>
            
            <div className="space-y-6">
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                <h3 className="font-semibold text-teal-800 mb-2">General Format</h3>
                <pre className="bg-white border rounded p-3 text-sm overflow-x-auto">
{`[Union]
sub_category_entity
--
super_category_entity1
super_category_entity2
super_category_entity3`}
                </pre>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">When to Use</h4>
                <p className="text-blue-700 text-sm">
                  Use union when you need to create a new entity that represents the combination of multiple existing entity types.
                </p>
              </div>

              <div className="bg-gray-100 border border-gray-300 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-2">Example</h4>
                <pre className="bg-white border rounded p-3 text-sm overflow-x-auto">
{`[Union]
Payable
--
Medication
Room
Instruction`}
                </pre>
              </div>
            </div>
          </section>

          {/* Complete Example Section */}
          <section id="example" className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              Complete Example
            </h2>
            
            <div className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="font-semibold text-green-800 mb-2">Employee-Department System</h3>
                <p className="text-green-700 text-sm">
                  This example demonstrates entities, relationships, and specializations working together.
                </p>
              </div>

              <div className="bg-gray-100 border border-gray-300 rounded-lg p-4">
                <pre className="bg-white border rounded p-4 text-sm overflow-x-auto">
{`[Entity]
Employee
--
FirstName
LastName
SSN @key
Address (street,city,state)
Phone @multivalued

[Entity]
SalaryEmployee
--
Salary

[Entity]
HourlyEmployee
--
Hour_rate

[Entity]
Department
--
Name
Location @multivalued
Number @key

[Entity]
Dependent @weak
--
name (firstname,lastname) @partial_key

[Relationship]
works_for
--
Employee (1) 
Department (N)

[Relationship]
Has @identifying
--
Employee (1)
Dependent (N) @total_participation

[Specialization]
Employee @disjoint
--
HourlyEmployee
SalaryEmployee`}
                </pre>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">What This Example Shows</h4>
                <ul className="text-blue-700 space-y-1 text-sm">
                  <li>• <strong>Strong entities:</strong> Employee, Department</li>
                  <li>• <strong>Weak entity:</strong> Dependent (depends on Employee)</li>
                  <li>• <strong>Key attributes:</strong> SSN, Number</li>
                  <li>• <strong>Composite attributes:</strong> Address, name</li>
                  <li>• <strong>Multivalued attributes:</strong> Phone, Location</li>
                  <li>• <strong>Partial key:</strong> name (for weak entity Dependent)</li>
                  <li>• <strong>Regular relationship:</strong> works_for</li>
                  <li>• <strong>Identifying relationship:</strong> Has (Employee-Dependent)</li>
                  <li>• <strong>Total participation:</strong> Dependent in Has relationship</li>
                  <li>• <strong>Disjoint specialization:</strong> Employee → HourlyEmployee, SalaryEmployee</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Best Practices */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              Best Practices
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-green-600">✅ Do</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    Use descriptive names for entities and relationships
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    Ensure all weak entities have identifying relationships
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    Use underscore (_) for multi-word names
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    Be consistent with naming conventions
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    Verify entity names match in relationships
                  </li>
                </ul>
              </div>
              
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-red-600">❌ Don't</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    Use spaces in entity or attribute names
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    Forget the attribute section (use empty if needed)
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    Mix hyphens (-) and underscores (_)
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    Create weak entities without identifying relationships
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    Use inconsistent cardinality notation
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Quick Reference Card */}
          <section className="bg-gradient-to-r from-blue-600 to-red-600 rounded-lg shadow-lg p-6 text-white">
            <h2 className="text-2xl font-bold mb-4 flex items-center">
                Quick Reference
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white bg-opacity-20 rounded-lg p-4">
                <h3 className="font-semibold mb-2">Entity Tags</h3>
                <div className="space-y-1 text-sm">
                  <div><code>@weak</code> - Weak entity</div>
                </div>
              </div>
              
              <div className="bg-white bg-opacity-20 rounded-lg p-4">
                <h3 className="font-semibold mb-2">Attribute Tags</h3>
                <div className="space-y-1 text-sm">
                  <div><code>@key</code> - Key attribute</div>
                  <div><code>@partial_key</code> - Partial key</div>
                  <div><code>@multivalued</code> - Multivalued</div>
                  <div><code>@derived</code> - Derived</div>
                </div>
              </div>
              
              <div className="bg-white bg-opacity-20 rounded-lg p-4">
                <h3 className="font-semibold mb-2">Relationship Tags</h3>
                <div className="space-y-1 text-sm">
                  <div><code>@identifying</code> - Identifying</div>
                  <div><code>@total_participation</code> - Total participation</div>
                  <div><code>@disjoint</code> - Disjoint specialization</div>
                  <div><code>@overlapping</code> - Overlapping specialization</div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                onClick={() => onNavigate('editor')}
                className="bg-white text-blue-600 hover:bg-gray-100 px-6 py-2 rounded-md font-medium transition-colors duration-200"
              >
                Start Creating ERD →
              </button>
              <button
                onClick={() => onNavigate('editor')}
                className="bg-transparent border border-white text-white hover:bg-white hover:text-blue-600 px-6 py-2 rounded-md font-medium transition-colors duration-200"
              >
                View Requirements
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ERDGuidelines;